import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { api, API_BASE, uploadImage } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Link from '@mui/material/Link';
import AdminLoading from './AdminLoading';
import AdminEmptyState from './AdminEmptyState';
import { IoImagesOutline, IoInformationCircleOutline } from 'react-icons/io5';
import { MdArrowDownward, MdArrowUpward } from 'react-icons/md';
import { INDIAN_FESTIVALS } from './festivalData';

const emptySlide = () => ({
  imageUrl: '',
  alt: '',
  link: '',
  title: '',
  subtitle: '',
});

/**
 * The fixed landing page rows shown in their own top section.
 * These are storefront pages that have heroes but are NOT CMS pages.
 */
const LANDING_PAGE_ROWS = [
  { context: 'shop', key: 'Festive vibes', label: 'Festive Vibes', path: '/festive-vibes', description: 'Default hero shown on the /festive-vibes page when no festival schedule is active.' },
  { context: 'shop', key: 'Occasion',      label: 'Occasions',     path: '/occasions',     description: 'Default hero shown on the /occasions page.' },
];

/** Shop URL ?category= keys: default, gender + nav presets (match Header links), then category names from API, then festival + static festival keys (deduped). */
function buildShopScopeRows(categories, festivalSchedules) {
  const names = Array.isArray(categories) ? categories.map((c) => c.name).filter(Boolean) : [];
  // Exclude keys already handled in the Landing Pages section above
  const landingKeys = new Set(LANDING_PAGE_ROWS.map((r) => r.key));
  const presetKeys = ['', 'Mens', 'Womens'];
  const presetLabels = {
    '': 'All browse (default)',
    Mens: 'Men (gender filter)',
    Womens: 'Women (gender filter)',
  };
  const seen = new Set([...presetKeys, ...landingKeys]);
  const rows = presetKeys.map((key) => ({ key, label: presetLabels[key] || key }));
  for (const n of names) {
    if (seen.has(n)) continue;
    seen.add(n);
    rows.push({ key: n, label: n });
  }
  // Add festival schedule context keys (from DB)
  if (Array.isArray(festivalSchedules)) {
    for (const f of festivalSchedules) {
      const k = f.context_key;
      if (!k || seen.has(k)) continue;
      seen.add(k);
      rows.push({ key: k, label: `Festival: ${f.name}` });
    }
  }
  // Add ALL static festival suggestedKeys so slider uploads from the calendar
  // never land in "Other saved heroes" — they show here labeled "Festival: Name"
  for (const f of INDIAN_FESTIVALS) {
    const k = f.suggestedKey;
    if (!k || seen.has(k)) continue;
    seen.add(k);
    rows.push({ key: k, label: `Festival: ${f.name}` });
  }
  return rows;
}

function findHero(heroes, context, contextKey) {
  const k = contextKey == null ? '' : String(contextKey);
  return (heroes || []).find((h) => h.context === context && (h.context_key || '') === k) || null;
}

function SlideImageRow({ slide, index, total, onChange, onMoveUp, onMoveDown, onRemove }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const inputRef = React.useRef(null);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr('');
    setUploading(true);
    try {
      const url = await uploadImage(file);
      onChange({ ...slide, imageUrl: url });
    } catch (ex) {
      setErr(ex.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <IconButton size="small" disabled={index === 0} onClick={onMoveUp} aria-label="Move up">
            <MdArrowUpward />
          </IconButton>
          <IconButton size="small" disabled={index >= total - 1} onClick={onMoveDown} aria-label="Move down">
            <MdArrowDownward />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField
              size="small"
              label="Image URL"
              value={slide.imageUrl || ''}
              onChange={(e) => onChange({ ...slide, imageUrl: e.target.value })}
              placeholder="/uploads/… or https://"
              fullWidth
              sx={{ flex: 1, minWidth: 180 }}
              error={!!err}
              helperText={err}
            />
            <Button component="label" variant="outlined" size="small" disabled={uploading} sx={{ flexShrink: 0 }}>
              {uploading ? '…' : 'Upload'}
              <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" hidden onChange={handleFile} />
            </Button>
          </Box>
          {slide.imageUrl ? (
            <Box
              component="img"
              src={slide.imageUrl.startsWith('http') ? slide.imageUrl : API_BASE + (slide.imageUrl.startsWith('/') ? slide.imageUrl : '/' + slide.imageUrl)}
              alt=""
              sx={{ mt: 1, maxHeight: 80, maxWidth: 200, objectFit: 'cover', border: 1, borderColor: 'divider', borderRadius: 1 }}
            />
          ) : null}
          <TextField
            size="small"
            label="Alt text"
            value={slide.alt || ''}
            onChange={(e) => onChange({ ...slide, alt: e.target.value })}
            fullWidth
            sx={{ mt: 1 }}
          />
          <TextField
            size="small"
            label="Link (optional)"
            value={slide.link || ''}
            onChange={(e) => onChange({ ...slide, link: e.target.value })}
            placeholder="/shop or https://"
            fullWidth
            sx={{ mt: 1 }}
          />
          <TextField
            size="small"
            label="Overlay title"
            value={slide.title || ''}
            onChange={(e) => onChange({ ...slide, title: e.target.value })}
            fullWidth
            sx={{ mt: 1 }}
          />
          <TextField
            size="small"
            label="Overlay subtitle"
            value={slide.subtitle || ''}
            onChange={(e) => onChange({ ...slide, subtitle: e.target.value })}
            fullWidth
            sx={{ mt: 1 }}
          />
          <Button size="small" color="error" sx={{ mt: 1 }} onClick={onRemove}>
            Remove slide
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

export default function AdminHeroSliders() {
  const [pages, setPages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [heroes, setHeroes] = useState([]);
  const [festivalSchedules, setFestivalSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogCtx, setDialogCtx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formSlides, setFormSlides] = useState([emptySlide()]);
  const [dialogError, setDialogError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [pagesData, heroesData, catRes, festivalData] = await Promise.all([
        api('/api/admin/pages'),
        api('/api/admin/hero-sliders'),
        fetch(`${API_BASE}/api/categories`).then((r) => (r.ok ? r.json() : [])),
        api('/api/admin/festival-schedules').catch(() => []),
      ]);
      setPages(Array.isArray(pagesData) ? pagesData : []);
      setHeroes(Array.isArray(heroesData) ? heroesData : []);
      setCategories(Array.isArray(catRes) ? catRes : []);
      setFestivalSchedules(Array.isArray(festivalData) ? festivalData : []);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const shopRows = useMemo(
    () => buildShopScopeRows(categories, festivalSchedules),
    [categories, festivalSchedules]
  );

  const pageSlugSet = useMemo(() => new Set((pages || []).map((p) => p.slug)), [pages]);
  const shopKeySet = useMemo(() => new Set(shopRows.map((r) => r.key)), [shopRows]);

  const landingKeySet = useMemo(() => new Set(LANDING_PAGE_ROWS.map((r) => r.key)), []);

  const orphans = useMemo(() => {
    return (heroes || []).filter((h) => {
      if (h.context === 'page') return !pageSlugSet.has(h.context_key || '');
      if (h.context === 'shop') {
        const k = h.context_key == null ? '' : String(h.context_key);
        return !shopKeySet.has(k) && !landingKeySet.has(k);
      }
      return true;
    });
  }, [heroes, pageSlugSet, shopKeySet, landingKeySet]);

  const openEditPage = (page, row) => {
    setDialogCtx({ type: 'page', page, row });
    setFormSlides(row?.slides?.length ? row.slides.map((s) => ({ ...emptySlide(), ...s })) : [emptySlide()]);
    setDialogError('');
    setDialogOpen(true);
  };

  const openEditLanding = (landing, row) => {
    setDialogCtx({ type: 'landing', landing, row });
    setFormSlides(row?.slides?.length ? row.slides.map((s) => ({ ...emptySlide(), ...s })) : [emptySlide()]);
    setDialogError('');
    setDialogOpen(true);
  };

  const openEditShop = (scope, row) => {
    setDialogCtx({ type: 'shop', scope, row });
    setFormSlides(row?.slides?.length ? row.slides.map((s) => ({ ...emptySlide(), ...s })) : [emptySlide()]);
    setDialogError('');
    setDialogOpen(true);
  };

  const openEditOrphan = (row) => {
    setDialogCtx({ type: 'orphan', row });
    setFormSlides(row?.slides?.length ? row.slides.map((s) => ({ ...emptySlide(), ...s })) : [emptySlide()]);
    setDialogError('');
    setDialogOpen(true);
  };

  const updateSlide = (i, s) => {
    setFormSlides((prev) => {
      const next = [...prev];
      next[i] = s;
      return next;
    });
  };

  const moveSlide = (i, dir) => {
    setFormSlides((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const removeSlide = (i) => {
    setFormSlides((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i)));
  };

  const addSlide = () => setFormSlides((prev) => [...prev, emptySlide()]);

  const handleSave = async () => {
    const slides = formSlides.filter((s) => (s.imageUrl || '').trim());
    const existingId = dialogCtx?.row?.id;

    if (slides.length === 0) {
      if (existingId) {
        if (!window.confirm('Remove all slides? This deletes the saved hero for this scope.')) return;
        setSaving(true);
        setDialogError('');
        try {
          await api(`/api/admin/hero-sliders/${existingId}`, { method: 'DELETE' });
          setDialogOpen(false);
          load();
        } catch (e) {
          setDialogError(e.message);
        } finally {
          setSaving(false);
        }
        return;
      }
      setDialogOpen(false);
      return;
    }

    setSaving(true);
    setDialogError('');
    try {
      if (dialogCtx.type === 'page') {
        const slug = dialogCtx.page.slug;
        if (existingId) {
          await api(`/api/admin/hero-sliders/${existingId}`, { method: 'PUT', body: JSON.stringify({ slides }) });
        } else {
          await api('/api/admin/hero-sliders', {
            method: 'POST',
            body: JSON.stringify({ context: 'page', context_key: slug, slides }),
          });
        }
      } else if (dialogCtx.type === 'landing') {
        const { context, key } = dialogCtx.landing;
        if (existingId) {
          await api(`/api/admin/hero-sliders/${existingId}`, { method: 'PUT', body: JSON.stringify({ slides }) });
        } else {
          await api('/api/admin/hero-sliders', {
            method: 'POST',
            body: JSON.stringify({ context, context_key: key, slides }),
          });
        }
      } else if (dialogCtx.type === 'shop') {
        const key = dialogCtx.scope.key;
        if (existingId) {
          await api(`/api/admin/hero-sliders/${existingId}`, { method: 'PUT', body: JSON.stringify({ slides }) });
        } else {
          await api('/api/admin/hero-sliders', {
            method: 'POST',
            body: JSON.stringify({ context: 'shop', context_key: key, slides }),
          });
        }
      } else if (dialogCtx.type === 'orphan') {
        await api(`/api/admin/hero-sliders/${dialogCtx.row.id}`, { method: 'PUT', body: JSON.stringify({ slides }) });
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      setDialogError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOrphan = async (row) => {
    if (!window.confirm('Delete this saved hero?')) return;
    try {
      await api(`/api/admin/hero-sliders/${row.id}`, { method: 'DELETE' });
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const dialogTitle = () => {
    if (!dialogCtx) return '';
    if (dialogCtx.type === 'page') return `Slides — ${dialogCtx.page.title || dialogCtx.page.slug}`;
    if (dialogCtx.type === 'landing') return `Slides — ${dialogCtx.landing.label} (${dialogCtx.landing.path})`;
    if (dialogCtx.type === 'shop') return `Slides — Shop: ${dialogCtx.scope.label}`;
    return `Slides — Other (${dialogCtx.row.context} / ${dialogCtx.row.context_key || '—'})`;
  };

  if (loading) return <AdminLoading />;

  // Only show pages that use page-context heroes (exclude shop-context landing pages handled above)
  const shopContextSlugs = new Set(LANDING_PAGE_ROWS.map((r) => r.key.toLowerCase().replace(/\s+/g, '-')));
  shopContextSlugs.add('festive-vibes');
  shopContextSlugs.add('occasions');
  shopContextSlugs.add('shop');
  const sortedPages = [...pages]
    .filter((p) => !shopContextSlugs.has(p.slug))
    .sort((a, b) => String(a.slug).localeCompare(String(b.slug)));

  return (
    <>
      {/* ── Storefront Landing Pages ─────────────────────────────────────── */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Storefront landing page heroes"
          subheader="Assign a default hero banner for each key landing page. The Festive Vibes hero switches automatically to a festival-specific banner when you activate a festival schedule."
        />
        <CardContent>
          <Alert severity="info" icon={<IoInformationCircleOutline />} sx={{ mb: 2, fontSize: '0.82rem' }}>
            <strong>How festival sliders work:</strong> Upload a slider under <em>Festive Vibes</em> below for the <strong>always-on</strong> default.
            To have a hero automatically swap during a specific festival (e.g. Diwali), go to <strong>Festival Schedule → My Schedules</strong> and create an active schedule — then upload the hero in the <em>Festival hero sliders</em> section on this page.
          </Alert>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Page</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell>Slides</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {LANDING_PAGE_ROWS.map((landing) => {
                  const row = findHero(heroes, landing.context, landing.key);
                  return (
                    <TableRow key={landing.key} hover>
                      <TableCell>
                        <Box>
                          <strong>{landing.label}</strong>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {landing.description}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Link href={`${window.location.origin.replace(':3000', '')}${landing.path}`} target="_blank" rel="noopener noreferrer">
                          {landing.path}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {row ? (
                          <Chip label={`${Array.isArray(row.slides) ? row.slides.length : 0} slide(s)`} size="small" color="success" variant="outlined" />
                        ) : (
                          <Chip label="No slides" size="small" color="warning" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>{row?.updated_at ? new Date(row.updated_at).toLocaleString() : '—'}</TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="contained" onClick={() => openEditLanding(landing, row)}>
                          {row ? 'Edit slides' : 'Add slides'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ── CMS Page hero banners ─────────────────────────────────────────── */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Page hero banners"
          subheader={
            <>
              One row per CMS page (3:1). Create pages under{' '}
              <Link component={RouterLink} to="/admin/pages">
                Pages
              </Link>
              , then add slides here. Slugs match URLs like <code>/your-slug</code>.{' '}
              <strong>Festive vibes</strong> and <strong>Occasion</strong> in the site header are shop category links, not CMS pages — use <strong>Shop hero banners</strong> below for those.
            </>
          }
        />
        <CardContent>
          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          {sortedPages.length === 0 ? (
            <AdminEmptyState message="No CMS pages yet. Create a page first, then return here to add banner slides." icon={IoImagesOutline} />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Page</TableCell>
                    <TableCell>Slug / URL</TableCell>
                    <TableCell>Slides</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedPages.map((p) => {
                    const row = findHero(heroes, 'page', p.slug);
                    return (
                      <TableRow key={p.slug} hover>
                        <TableCell>
                          <strong>{p.title || p.slug}</strong>
                        </TableCell>
                        <TableCell>
                          <Link href={`${window.location.origin}/${encodeURIComponent(p.slug)}`} target="_blank" rel="noopener noreferrer">
                            /{p.slug}
                          </Link>
                        </TableCell>
                        <TableCell>{row ? (Array.isArray(row.slides) ? row.slides.length : 0) : 0}</TableCell>
                        <TableCell>{row?.updated_at ? new Date(row.updated_at).toLocaleString() : '—'}</TableCell>
                        <TableCell align="right">
                          <Button size="small" variant="contained" onClick={() => openEditPage(p, row)}>
                            {row ? 'Edit slides' : 'Add slides'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Festival & category hero sliders"
          subheader="Per-category and per-festival hero banners shown on the /shop browse page. The slider matching the active ?category= filter is shown. Festival sliders activate when you create an active festival schedule."
        />
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Scope</TableCell>
                  <TableCell>URL key</TableCell>
                  <TableCell>Slides</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shopRows.map((scope) => {
                  const row = findHero(heroes, 'shop', scope.key);
                  return (
                    <TableRow key={scope.key || '__default'} hover>
                      <TableCell>{scope.label}</TableCell>
                      <TableCell>
                        <code>{scope.key === '' ? '(empty — /shop)' : `?category=${scope.key}`}</code>
                      </TableCell>
                      <TableCell>{row ? (Array.isArray(row.slides) ? row.slides.length : 0) : 0}</TableCell>
                      <TableCell>{row?.updated_at ? new Date(row.updated_at).toLocaleString() : '—'}</TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="contained" onClick={() => openEditShop(scope, row)}>
                          {row ? 'Edit slides' : 'Add slides'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {orphans.length > 0 ? (
        <Card>
          <CardHeader
            title="Other saved heroes"
            subheader="Leftover entries (e.g. old page slug or category renamed). You can edit, delete, or ignore."
          />
          <CardContent>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Context</TableCell>
                    <TableCell>Key</TableCell>
                    <TableCell>Slides</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orphans.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.context}</TableCell>
                      <TableCell>
                        <strong>{row.context_key || '—'}</strong>
                      </TableCell>
                      <TableCell>{Array.isArray(row.slides) ? row.slides.length : 0}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => openEditOrphan(row)}>
                          Edit
                        </Button>
                        <Button size="small" color="error" onClick={() => handleDeleteOrphan(row)}>
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ) : null}

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{dialogTitle()}</DialogTitle>
        <DialogContent>
          {dialogError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {dialogError}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Wide images (~3:1) work best. Display uses <code>object-fit: cover</code>. Save with no images to remove the hero for this scope.
          </Typography>
          {formSlides.map((slide, index) => (
            <SlideImageRow
              key={index}
              slide={slide}
              index={index}
              total={formSlides.length}
              onChange={(s) => updateSlide(index, s)}
              onMoveUp={() => moveSlide(index, -1)}
              onMoveDown={() => moveSlide(index, 1)}
              onRemove={() => removeSlide(index)}
            />
          ))}
          <Button size="small" variant="outlined" onClick={addSlide}>
            Add slide
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
